import { z } from 'zod'
import { PickupPointBookingQuestionTypeEnum } from '../helpers'
import {
  AdditionalDetailsContext,
  CarHireAdditionalDetailsContext,
  COLLECT_MAIN_ACCOUNT_HOLDER_CONTACT_DETAILS,
  COLLECT_NOTHING,
  COLLECT_PRIMARY_DRIVER_CONTACT_DETAILS,
  COLLECT_PRIMARY_DRIVER_EMAIL_ADDRESS,
  ExperienceAdditionalDetailsContext,
  HotelAdditionalDetailsContext,
} from '../helpers/additional-details-context'
import {
  createNonEmptyStringSchema,
  fieldCannotBeMoreThanNChars,
  fieldMustBeSelected,
  fieldNotValid,
  getTranslationMessage,
  TranslationFunc,
  notRequiredSchema,
  optionalField,
  createPhoneNumberSchema,
} from '../common'
import { createAccountHolderEmailAddressSchema, createAddressSchema } from './traveler-details'

export const MAX_SPECIAL_REQUIREMENTS_LENGTH = 250

const createContactDetailsSchema = (t?: TranslationFunc) =>
  z.object({
    address: createAddressSchema(t),
    emailAddress: createAccountHolderEmailAddressSchema(t),
    phone: createPhoneNumberSchema(t),
  })

export const createAccountHolderDataCollectionSchema = (t?: TranslationFunc) =>
  z.discriminatedUnion('collectionMethod', [
    z.object({
      collectionMethod: z.enum([
        COLLECT_MAIN_ACCOUNT_HOLDER_CONTACT_DETAILS,
        COLLECT_PRIMARY_DRIVER_CONTACT_DETAILS,
      ]),
      data: createContactDetailsSchema(t),
    }),
    z.object({
      collectionMethod: z.literal(COLLECT_PRIMARY_DRIVER_EMAIL_ADDRESS),
      data: z.object({ emailAddress: createAccountHolderEmailAddressSchema(t) }),
    }),
    z.object({
      collectionMethod: z.literal(COLLECT_NOTHING),
      data: notRequiredSchema,
    }),
  ])

const createHotelAdditionalDetailsSchema = (
  hotel?: HotelAdditionalDetailsContext,
  t?: TranslationFunc
) => {
  if (!hotel) {
    return notRequiredSchema
  }

  return z.object({
    specialRequirements: z
      .string()
      .max(
        MAX_SPECIAL_REQUIREMENTS_LENGTH,
        getTranslationMessage({
          key: fieldCannotBeMoreThanNChars,
          field: 'pages.orders.additional_details.labels.special_requirements',
          t,
          values: {
            n: MAX_SPECIAL_REQUIREMENTS_LENGTH,
          },
        })
      )
      .optional(),
  })
}

const createCarHireAdditionalDetailsSchema = (
  carHire?: CarHireAdditionalDetailsContext,
  t?: TranslationFunc
) => {
  if (!carHire) {
    return notRequiredSchema
  }

  return z.object({
    flightNumber: z
      .string()
      .trim()
      .toUpperCase()
      .regex(
        /^[A-Z]{2}\d{1,6}$/,
        getTranslationMessage({
          key: fieldNotValid,
          field: 'pages.orders.additional_details.car_hire.flight_number',
          t,
        })
      )
      .optional()
      .or(z.literal('')),
    avisPreferredMembershipNumber: z
      .string()
      .trim()
      .toUpperCase()
      .regex(
        /^[A-Za-z0-9]{1,6}$/,
        getTranslationMessage({
          key: fieldNotValid,
          field: 'pages.orders.additional_details.car_hire.avis_preferred_membership_number',
          t,
        })
      )
      .optional()
      .or(z.literal('')),
  })
}

const createPickupPointSchema = (
  experience: ExperienceAdditionalDetailsContext,
  t?: TranslationFunc
) => {
  const { pickupPointBookingQuestionType, pickupLocations } = experience

  const pickupLocationMustBeSelectedErrorMessage = getTranslationMessage({
    key: fieldMustBeSelected,
    field: 'pages.orders.additional_details.labels.pickup_location',
    t,
  })

  const pickupLocationIds = pickupLocations?.map(({ id }) => id)
  const locationReferenceSchema =
    pickupLocationIds?.length &&
    z.enum([pickupLocationIds[0], ...pickupLocationIds], pickupLocationMustBeSelectedErrorMessage)

  const freeTextSchema = createNonEmptyStringSchema({
    field: 'pages.orders.additional_details.labels.pickup_location',
    t,
  })

  const locationReferenceLiteral = z.literal(
    PickupPointBookingQuestionTypeEnum.LOCATION_REFERENCE,
    pickupLocationMustBeSelectedErrorMessage
  )
  const freeTextLiteral = z.literal(PickupPointBookingQuestionTypeEnum.FREETEXT)

  const isFreeText = pickupPointBookingQuestionType === PickupPointBookingQuestionTypeEnum.FREETEXT
  const isLocationReference =
    pickupPointBookingQuestionType === PickupPointBookingQuestionTypeEnum.LOCATION_REFERENCE
  const isLocationReferenceOrFreeText =
    pickupPointBookingQuestionType ===
    PickupPointBookingQuestionTypeEnum.LOCATION_REFERENCE_OR_FREETEXT

  if (locationReferenceSchema && (isLocationReference || isLocationReferenceOrFreeText)) {
    if (isLocationReference) {
      return z.object({
        type: locationReferenceLiteral,
        value: locationReferenceSchema,
      })
    }

    return z.object({
      type: z.union([locationReferenceLiteral, freeTextLiteral]),
      value: z.union([locationReferenceSchema, freeTextSchema]),
    })
  }

  if (isFreeText) {
    return z.object({
      type: freeTextLiteral,
      value: freeTextSchema,
    })
  }

  return notRequiredSchema
}

const createLanguageGuideSchema = (
  { languageGuideOptions }: ExperienceAdditionalDetailsContext,
  t?: TranslationFunc
) => {
  const languageGuideSchemas = languageGuideOptions?.map(({ language, type }) =>
    z.object(
      { language: z.literal(language), type: z.literal(type) },
      getTranslationMessage({
        key: fieldMustBeSelected,
        field: 'pages.orders.additional_details.labels.language_guide',
        t,
      })
    )
  )

  if (!languageGuideSchemas || languageGuideSchemas.length === 0) {
    return notRequiredSchema
  }

  if (languageGuideSchemas.length > 1) {
    return z.union(
      [languageGuideSchemas[0], languageGuideSchemas[1], ...languageGuideSchemas.slice(2)],
      getTranslationMessage({
        key: fieldMustBeSelected,
        field: 'pages.orders.additional_details.labels.language_guide',
        t,
      })
    )
  }
  return languageGuideSchemas[0]
}

const createExperienceAdditionalDetailsSchema = (
  experience?: ExperienceAdditionalDetailsContext,
  t?: TranslationFunc
) => {
  if (!experience) {
    return notRequiredSchema
  }
  const pickupPointSchema = createPickupPointSchema(experience, t)
  const { specialRequirements } = experience
  return z.object({
    specialRequirements: optionalField(
      specialRequirements
        ? z
            .string()
            .max(
              specialRequirements.maxLength ?? MAX_SPECIAL_REQUIREMENTS_LENGTH,
              getTranslationMessage({
                key: fieldCannotBeMoreThanNChars,
                field: 'pages.orders.additional_details.labels.special_requirements',
                t,
                values: {
                  n: specialRequirements.maxLength ?? MAX_SPECIAL_REQUIREMENTS_LENGTH,
                },
              })
            )
            .optional()
        : notRequiredSchema
    ),
    pickupPoint: optionalField(pickupPointSchema),
    languageGuide: optionalField(createLanguageGuideSchema(experience, t)),
  })
}

export const createAdditionalDetailsSchema = (
  additionalDetailsContext: AdditionalDetailsContext,
  t?: TranslationFunc
) => {
  const { accountHolderDataCollectionMethod, hotel, carHire, experience } = additionalDetailsContext
  return z
    .object({
      hotel: optionalField(createHotelAdditionalDetailsSchema(hotel, t)),
      carHire: optionalField(createCarHireAdditionalDetailsSchema(carHire, t)),
      experience: optionalField(createExperienceAdditionalDetailsSchema(experience, t)),
    })
    .and(
      z.object({
        accountHolder: createAccountHolderDataCollectionSchema(t).refine(
          (val) => val.collectionMethod === accountHolderDataCollectionMethod
        ),
      })
    )
}

export type AdditionalDetails = z.infer<ReturnType<typeof createAdditionalDetailsSchema>>
